import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Navigation, 
    Plus, 
    Eye, 
    CheckCircle, 
    Clock, 
    Fuel, 
    Gauge, 
    Ship,
    MapPin,
    PauseCircle,
    Filter,
    Search,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const statusLabels = {
    em_andamento: { label: 'Em Andamento', class: 'badge-warning' },
    concluida: { label: 'Concluída', class: 'badge-success' },
    cancelada: { label: 'Cancelada', class: 'badge-danger' }
};

const ViagensPage = () => {
    const [viagens, setViagens] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [allEmpurradores, setAllEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [destinos, setDestinos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [finalizarDialogOpen, setFinalizarDialogOpen] = useState(false);
    const [paradaDialogOpen, setParadaDialogOpen] = useState(false);
    const [selectedViagem, setSelectedViagem] = useState(null);
    const { hasRole } = useAuth();
    
    // Filtros
    const [filters, setFilters] = useState({
        data_inicio: '',
        data_fim: '',
        status: '',
        empurrador_id: ''
    });

    const [formData, setFormData] = useState({
        empurrador_id: '',
        comandante: '',
        origem_id: '',
        destino_id: '',
        data_hora_saida: '',
        balsa_1_id: '',
        balsa_2_id: '',
        balsa_3_id: '',
        balsa_4_id: '',
        combustivel_saida: '',
        observacoes: ''
    });

    const [finalizarData, setFinalizarData] = useState({
        data_hora_chegada: '',
        combustivel_chegada: ''
    });

    const [paradaData, setParadaData] = useState({
        local: '',
        horario_inicio: '',
        horario_termino: '',
        motivo: ''
    });

    useEffect(() => {
        fetchBaseData();
    }, []);
    
    useEffect(() => {
        fetchViagens();
    }, [filters]);

    const fetchBaseData = async () => {
        try {
            const [empurradoresRes, balsasRes, destinosRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`),
                axios.get(`${API}/destinos`)
            ]);
            setAllEmpurradores(empurradoresRes.data);
            setEmpurradores(empurradoresRes.data.filter(e => e.status === 'ativo'));
            setBalsas(balsasRes.data.filter(b => b.status === 'ativa'));
            setDestinos(destinosRes.data.filter(d => d.ativo));
        } catch (error) {
            toast.error('Erro ao carregar dados base');
        }
    };

    const fetchViagens = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.data_inicio) params.data_inicio = filters.data_inicio;
            if (filters.data_fim) params.data_fim = filters.data_fim;
            if (filters.status && filters.status !== 'all') params.status = filters.status;
            if (filters.empurrador_id && filters.empurrador_id !== 'all') params.empurrador_id = filters.empurrador_id;
            
            const response = await axios.get(`${API}/viagens`, { params });
            setViagens(response.data);
        } catch (error) {
            toast.error('Erro ao carregar viagens');
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        fetchBaseData();
        fetchViagens();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Get origem name from destinos list
        const origemDest = destinos.find(d => d.id === formData.origem_id);
        
        const data = {
            ...formData,
            origem: origemDest?.nome || formData.origem_id,
            origem_id: formData.origem_id || null,
            combustivel_saida: formData.combustivel_saida ? parseFloat(formData.combustivel_saida) : null,
            data_hora_saida: new Date(formData.data_hora_saida).toISOString(),
            balsa_1_id: formData.balsa_1_id || null,
            balsa_2_id: formData.balsa_2_id || null,
            balsa_3_id: formData.balsa_3_id || null,
            balsa_4_id: formData.balsa_4_id || null
        };
        
        try {
            await axios.post(`${API}/viagens`, data);
            toast.success('Viagem registrada!');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar viagem');
        }
    };

    const handleFinalizar = async (e) => {
        e.preventDefault();
        
        try {
            await axios.post(`${API}/viagens/${selectedViagem.id}/finalizar`, null, {
                params: {
                    data_hora_chegada: new Date(finalizarData.data_hora_chegada).toISOString(),
                    combustivel_chegada: parseFloat(finalizarData.combustivel_chegada)
                }
            });
            toast.success('Viagem finalizada com sucesso!');
            setFinalizarDialogOpen(false);
            setFinalizarData({ data_hora_chegada: '', combustivel_chegada: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao finalizar viagem');
        }
    };

    const handleAddParada = async (e) => {
        e.preventDefault();
        
        try {
            const parada = {
                local: paradaData.local,
                horario_inicio: new Date(paradaData.horario_inicio).toISOString(),
                horario_termino: paradaData.horario_termino ? new Date(paradaData.horario_termino).toISOString() : null,
                motivo: paradaData.motivo || null
            };
            
            await axios.post(`${API}/viagens/${selectedViagem.id}/parada`, parada);
            toast.success('Parada registrada!');
            setParadaDialogOpen(false);
            setParadaData({ local: '', horario_inicio: '', horario_termino: '', motivo: '' });
            
            // Refresh viagem details
            const response = await axios.get(`${API}/viagens/${selectedViagem.id}`);
            setSelectedViagem(response.data);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar parada');
        }
    };

    const resetForm = () => {
        setFormData({
            empurrador_id: '',
            comandante: '',
            origem_id: '',
            destino_id: '',
            data_hora_saida: '',
            balsa_1_id: '',
            balsa_2_id: '',
            balsa_3_id: '',
            balsa_4_id: '',
            combustivel_saida: '',
            observacoes: ''
        });
    };

    const handleDeleteViagem = async (viagemId, numeroViagem) => {
        if (!window.confirm(`Tem certeza que deseja excluir a viagem ${numeroViagem}?\n\nTodos os dados da viagem serão removidos permanentemente.\nEsta ação não pode ser desfeita.`)) {
            return;
        }
        
        try {
            await axios.delete(`${API}/viagens/${viagemId}`);
            toast.success('Viagem excluída com sucesso');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir viagem');
        }
    };

    const getEmpurradorNome = (id) => allEmpurradores.find(e => e.id === id)?.nome || empurradores.find(e => e.id === id)?.nome || '-';
    const getBalsaNome = (id) => balsas.find(b => b.id === id)?.nome || null;
    const getDestinoNome = (id) => destinos.find(d => d.id === id)?.nome || '-';

    const canEdit = hasRole(['admin', 'gestor', 'operador']);

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '-';
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hours > 0) {
            return `${hours}h ${mins}min`;
        }
        return `${mins}min`;
    };

    const calcTotalParadas = (paradas) => {
        if (!paradas || paradas.length === 0) return 0;
        return paradas.reduce((acc, p) => acc + (p.duracao_minutos || 0), 0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="viagens-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Monitoramento de Viagens
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Registro e acompanhamento de viagens com indicadores automáticos
                    </p>
                </div>
                
                {canEdit && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-viagem"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Nova Viagem
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Registrar Nova Viagem
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                {/* Dados Gerais */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2">
                                        Dados Gerais
                                    </h3>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Empurrador *
                                            </Label>
                                            <Select 
                                                value={formData.empurrador_id} 
                                                onValueChange={(value) => setFormData({...formData, empurrador_id: value})}
                                            >
                                                <SelectTrigger data-testid="select-viagem-empurrador" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {empurradores.map((emp) => (
                                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Comandante *
                                            </Label>
                                            <Input
                                                data-testid="input-viagem-comandante"
                                                value={formData.comandante}
                                                onChange={(e) => setFormData({...formData, comandante: e.target.value})}
                                                required
                                                placeholder="Nome do comandante"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Origem *
                                            </Label>
                                            <Select 
                                                value={formData.origem_id || "none"} 
                                                onValueChange={(value) => setFormData({...formData, origem_id: value === "none" ? "" : value})}
                                            >
                                                <SelectTrigger data-testid="select-viagem-origem" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    <SelectItem value="none">Selecione a origem</SelectItem>
                                                    {destinos.map((dest) => (
                                                        <SelectItem key={dest.id} value={dest.id}>
                                                            {dest.nome}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Destino *
                                            </Label>
                                            <Select 
                                                value={formData.destino_id} 
                                                onValueChange={(value) => setFormData({...formData, destino_id: value})}
                                            >
                                                <SelectTrigger data-testid="select-viagem-destino" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {destinos.map((dest) => (
                                                        <SelectItem key={dest.id} value={dest.id}>
                                                            {dest.nome} ({dest.distancia_milhas} mn)
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Data/Hora Saída *
                                            </Label>
                                            <Input
                                                type="datetime-local"
                                                data-testid="input-viagem-saida"
                                                value={formData.data_hora_saida}
                                                onChange={(e) => setFormData({...formData, data_hora_saida: e.target.value})}
                                                required
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Combustível Inicial (L) *
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                data-testid="input-combustivel-inicial"
                                                value={formData.combustivel_saida}
                                                onChange={(e) => setFormData({...formData, combustivel_saida: e.target.value})}
                                                placeholder="Litros no tanque"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Composição do Comboio */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2">
                                        Composição do Comboio (até 4 balsas)
                                    </h3>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        {[1, 2, 3, 4].map((num) => (
                                            <div key={num} className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Balsa {num}
                                                </Label>
                                                <Select 
                                                    value={formData[`balsa_${num}_id`] || "none"} 
                                                    onValueChange={(value) => setFormData({...formData, [`balsa_${num}_id`]: value === "none" ? "" : value})}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                        <SelectValue placeholder="Selecione..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="none">Nenhuma</SelectItem>
                                                        {balsas.map((balsa) => (
                                                            <SelectItem key={balsa.id} value={balsa.id}>
                                                                {balsa.nome} {balsa.porte_bruto ? `(${balsa.porte_bruto}t)` : ''}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Observações */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Observações sobre a viagem..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-viagem"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Iniciar Viagem
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Filtros */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-mono uppercase tracking-wider text-cyan-400">Filtros</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Início</Label>
                            <Input
                                type="date"
                                data-testid="filter-data-inicio"
                                value={filters.data_inicio}
                                onChange={(e) => setFilters({...filters, data_inicio: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Fim</Label>
                            <Input
                                type="date"
                                data-testid="filter-data-fim"
                                value={filters.data_fim}
                                onChange={(e) => setFilters({...filters, data_fim: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Status</Label>
                            <Select 
                                value={filters.status || "all"} 
                                onValueChange={(value) => setFilters({...filters, status: value === "all" ? "" : value})}
                            >
                                <SelectTrigger data-testid="filter-status" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                    <SelectItem value="concluida">Concluída</SelectItem>
                                    <SelectItem value="cancelada">Cancelada</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Empurrador</Label>
                            <Select 
                                value={filters.empurrador_id || "all"} 
                                onValueChange={(value) => setFilters({...filters, empurrador_id: value === "all" ? "" : value})}
                            >
                                <SelectTrigger data-testid="filter-empurrador" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {allEmpurradores.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                onClick={() => setFilters({ data_inicio: '', data_fim: '', status: '', empurrador_id: '' })}
                                variant="outline"
                                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm h-9"
                                data-testid="btn-clear-filters"
                            >
                                Limpar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-blue-500/20 rounded-sm">
                            <Navigation className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{viagens.length}</p>
                            <p className="text-xs text-slate-400">Total de Viagens</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-yellow-500/20 rounded-sm">
                            <Clock className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {viagens.filter(v => v.status === 'em_andamento').length}
                            </p>
                            <p className="text-xs text-slate-400">Em Andamento</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-green-500/20 rounded-sm">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {viagens.filter(v => v.status === 'concluida').length}
                            </p>
                            <p className="text-xs text-slate-400">Concluídas</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-cyan-500/20 rounded-sm">
                            <Fuel className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {viagens.reduce((acc, v) => acc + (v.consumo_total || 0), 0).toLocaleString('pt-BR')}
                            </p>
                            <p className="text-xs text-slate-400">Consumo Total (L)</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {viagens.length === 0 ? (
                        <div className="p-12 text-center">
                            <Navigation className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhuma viagem registrada</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nº Viagem</TableHead>
                                    <TableHead>Empurrador</TableHead>
                                    <TableHead>Comandante</TableHead>
                                    <TableHead>Destino</TableHead>
                                    <TableHead>Saída</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {viagens.map((viagem) => (
                                    <TableRow key={viagem.id} data-testid={`viagem-row-${viagem.id}`}>
                                        <TableCell className="font-mono text-cyan-400">
                                            {viagem.numero_viagem}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {getEmpurradorNome(viagem.empurrador_id)}
                                        </TableCell>
                                        <TableCell className="text-slate-300">
                                            {viagem.comandante}
                                        </TableCell>
                                        <TableCell className="text-slate-300">
                                            {getDestinoNome(viagem.destino_id)}
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {formatDateTime(viagem.data_hora_saida)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${statusLabels[viagem.status]?.class || 'badge-info'}
                                            `}>
                                                {statusLabels[viagem.status]?.label || viagem.status}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedViagem(viagem);
                                                        setDetailDialogOpen(true);
                                                    }}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {viagem.status === 'em_andamento' && canEdit && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedViagem(viagem);
                                                                setParadaDialogOpen(true);
                                                            }}
                                                            className="text-yellow-400 hover:text-yellow-300 hover:bg-slate-800"
                                                            title="Registrar Parada"
                                                        >
                                                            <PauseCircle className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedViagem(viagem);
                                                                setFinalizarDialogOpen(true);
                                                            }}
                                                            className="text-green-400 hover:text-green-300 hover:bg-slate-800"
                                                            title="Finalizar Viagem"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                {hasRole(['admin']) && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDeleteViagem(viagem.id, viagem.numero_viagem);
                                                        }}
                                                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                                        title="Excluir Viagem"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Navigation className="w-5 h-5 text-cyan-400" />
                            {selectedViagem?.numero_viagem}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedViagem && (
                        <div className="space-y-6 mt-4">
                            {/* Info Geral */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Empurrador</p>
                                    <p className="text-white mt-1">{getEmpurradorNome(selectedViagem.empurrador_id)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Comandante</p>
                                    <p className="text-white mt-1">{selectedViagem.comandante}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Origem</p>
                                    <p className="text-white mt-1">{selectedViagem.origem}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Destino</p>
                                    <p className="text-white mt-1">{getDestinoNome(selectedViagem.destino_id)}</p>
                                </div>
                            </div>

                            {/* Composição */}
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase mb-2">Composição do Comboio</p>
                                <div className="flex flex-wrap gap-2">
                                    {[1, 2, 3, 4].map(num => {
                                        const balsaNome = getBalsaNome(selectedViagem[`balsa_${num}_id`]);
                                        return balsaNome ? (
                                            <span key={num} className="px-2 py-1 bg-slate-800 rounded-sm text-sm text-slate-300 flex items-center gap-1">
                                                <Ship className="w-3 h-3" />
                                                {balsaNome}
                                            </span>
                                        ) : null;
                                    })}
                                    {!getBalsaNome(selectedViagem.balsa_1_id) && (
                                        <span className="text-slate-500">Nenhuma balsa</span>
                                    )}
                                </div>
                            </div>

                            {/* Controle de Combustível */}
                            <div className="space-y-3">
                                <p className="text-xs text-slate-500 font-mono uppercase flex items-center gap-2">
                                    <Fuel className="w-4 h-4 text-orange-400" />
                                    Controle de Combustível
                                </p>
                                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-800/30 rounded-sm border border-slate-700">
                                    <div className="text-center">
                                        <p className="text-[10px] text-slate-500 uppercase mb-1">Inicial</p>
                                        <p className="text-xl font-bold text-green-400">
                                            {selectedViagem.combustivel_saida?.toLocaleString('pt-BR') || '-'}
                                        </p>
                                        <p className="text-[10px] text-slate-500">Litros</p>
                                    </div>
                                    <div className="text-center border-x border-slate-700">
                                        <p className="text-[10px] text-slate-500 uppercase mb-1">Final</p>
                                        <p className="text-xl font-bold text-blue-400">
                                            {selectedViagem.combustivel_chegada?.toLocaleString('pt-BR') || '-'}
                                        </p>
                                        <p className="text-[10px] text-slate-500">Litros</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[10px] text-slate-500 uppercase mb-1">Consumido</p>
                                        <p className="text-xl font-bold text-orange-400">
                                            {selectedViagem.consumo_total?.toLocaleString('pt-BR') || '-'}
                                        </p>
                                        <p className="text-[10px] text-slate-500">Litros</p>
                                    </div>
                                </div>
                            </div>

                            {/* Indicadores */}
                            {selectedViagem.status === 'concluida' && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-800/50 rounded-sm">
                                    <div className="text-center">
                                        <MapPin className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-white">{selectedViagem.milhas_percorridas || 0}</p>
                                        <p className="text-[10px] text-slate-500 uppercase">Milhas</p>
                                    </div>
                                    <div className="text-center">
                                        <Fuel className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-white">{selectedViagem.consumo_total?.toLocaleString('pt-BR') || 0}</p>
                                        <p className="text-[10px] text-slate-500 uppercase">Consumo (L)</p>
                                    </div>
                                    <div className="text-center">
                                        <Gauge className="w-5 h-5 text-green-400 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-white">{selectedViagem.velocidade_media || 0}</p>
                                        <p className="text-[10px] text-slate-500 uppercase">Vel. Média (nós)</p>
                                    </div>
                                    <div className="text-center">
                                        <Fuel className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-white">{selectedViagem.consumo_por_hora || 0}</p>
                                        <p className="text-[10px] text-slate-500 uppercase">Consumo (L/h)</p>
                                    </div>
                                </div>
                            )}

                            {/* Tempos */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data/Hora Saída</p>
                                    <p className="text-white mt-1">{formatDateTime(selectedViagem.data_hora_saida)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data/Hora Chegada</p>
                                    <p className="text-white mt-1">{formatDateTime(selectedViagem.data_hora_chegada)}</p>
                                </div>
                            </div>

                            {/* Paradas */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs text-slate-500 font-mono uppercase flex items-center gap-2">
                                        <PauseCircle className="w-4 h-4" />
                                        Paradas ({selectedViagem.paradas?.length || 0})
                                        {selectedViagem.paradas?.length > 0 && (
                                            <span className="text-yellow-400">
                                                - Total: {formatDuration(calcTotalParadas(selectedViagem.paradas))}
                                            </span>
                                        )}
                                    </p>
                                    {selectedViagem.status === 'em_andamento' && canEdit && (
                                        <Button
                                            size="sm"
                                            onClick={() => setParadaDialogOpen(true)}
                                            className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-sm h-7 text-xs"
                                        >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Registrar Parada
                                        </Button>
                                    )}
                                </div>
                                
                                {selectedViagem.paradas && selectedViagem.paradas.length > 0 ? (
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                                        {selectedViagem.paradas.map((parada, idx) => (
                                            <div key={idx} className="p-3 bg-slate-800/50 rounded-sm border-l-2 border-yellow-500">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-white font-medium text-sm flex items-center gap-2">
                                                            <MapPin className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                                                            {parada.local}
                                                        </p>
                                                        {parada.motivo && (
                                                            <div className="mt-2 max-h-24 overflow-y-auto">
                                                                <p className="text-slate-400 text-xs whitespace-pre-wrap break-words">{parada.motivo}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-yellow-400 font-mono text-xs flex-shrink-0 ml-2">
                                                        {formatDuration(parada.duracao_minutos)}
                                                    </span>
                                                </div>
                                                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                                    <span>Início: {formatDateTime(parada.horario_inicio)}</span>
                                                    {parada.horario_termino && (
                                                        <span>Fim: {formatDateTime(parada.horario_termino)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-slate-500 text-sm italic">Nenhuma parada registrada</p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Parada Dialog */}
            <Dialog open={paradaDialogOpen} onOpenChange={setParadaDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <PauseCircle className="w-5 h-5 text-yellow-400" />
                            Registrar Parada
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleAddParada} className="space-y-4 mt-4">
                        <p className="text-sm text-slate-400">
                            Viagem: <span className="text-cyan-400 font-mono">{selectedViagem?.numero_viagem}</span>
                        </p>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Local da Parada *
                            </Label>
                            <Input
                                value={paradaData.local}
                                onChange={(e) => setParadaData({...paradaData, local: e.target.value})}
                                required
                                placeholder="Ex: Porto de Vila do Conde"
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Início da Parada *
                                </Label>
                                <Input
                                    type="datetime-local"
                                    value={paradaData.horario_inicio}
                                    onChange={(e) => setParadaData({...paradaData, horario_inicio: e.target.value})}
                                    required
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Fim da Parada
                                </Label>
                                <Input
                                    type="datetime-local"
                                    value={paradaData.horario_termino}
                                    onChange={(e) => setParadaData({...paradaData, horario_termino: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Motivo
                            </Label>
                            <Textarea
                                value={paradaData.motivo}
                                onChange={(e) => setParadaData({...paradaData, motivo: e.target.value})}
                                placeholder="Motivo da parada (descarga, abastecimento, etc.)"
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setParadaDialogOpen(false);
                                    setParadaData({ local: '', horario_inicio: '', horario_termino: '', motivo: '' });
                                }}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-sm"
                            >
                                Registrar Parada
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Finalizar Dialog */}
            <Dialog open={finalizarDialogOpen} onOpenChange={setFinalizarDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            Finalizar Viagem
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleFinalizar} className="space-y-4 mt-4">
                        <p className="text-sm text-slate-400">
                            Viagem: <span className="text-cyan-400 font-mono">{selectedViagem?.numero_viagem}</span>
                        </p>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Data/Hora Chegada *
                            </Label>
                            <Input
                                type="datetime-local"
                                value={finalizarData.data_hora_chegada}
                                onChange={(e) => setFinalizarData({...finalizarData, data_hora_chegada: e.target.value})}
                                required
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Combustível na Chegada (L) *
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={finalizarData.combustivel_chegada}
                                onChange={(e) => setFinalizarData({...finalizarData, combustivel_chegada: e.target.value})}
                                required
                                placeholder="Litros restantes"
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setFinalizarDialogOpen(false)}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-sm"
                            >
                                Finalizar Viagem
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ViagensPage;
